<template>
	<div class="tab-navbar" :class="{ 'tab-dark': dark }">
		<div
			class="tab-navbar__content"
			:class="{ 'tab-navbar--fixed': fixed, 'tab-navbar--shadow': shadow, 'tab-navbar--border': border }"
			:style="{ backgroundColor: themeBgColor }"
		>
			<div
				class="tab-navbar__header"
				:style="{ color: themeColor, backgroundColor: themeBgColor, height: navbarHeight }"
			>
				<!-- 左侧 -->
				<div
					class="tab-navbar__header-btns tab-navbar__header-btns-left"
					:style="{ width: leftIconWidth }"
					@click="onClickLeft"
				>
					<slot name="left">
						<span v-if="leftIcon" class="iconfont" :class="`icon-${leftIcon}`" :style="{ color: themeColor }" />
						<span v-if="leftText" class="tab-navbar-btn-text" :style="{ color: themeColor, fontSize: '12px' }">
							{{ leftText }}
						</span>
					</slot>
				</div>

				<!-- 中间标题 -->
				<div class="tab-navbar__header-container" @click="onClickTitle">
					<slot>
						<div v-if="title" class="tab-navbar__header-container-inner">
							<span class="tab-nav-bar-text tab-ellipsis-1" :style="{ color: themeColor }">{{ title }}</span>
						</div>
					</slot>
				</div>

				<!-- 右侧 -->
				<div
					class="tab-navbar__header-btns tab-navbar__header-btns-right"
					:style="{ width: rightIconWidth }"
					@click="onClickRight"
				>
					<slot name="right">
						<span v-if="rightIcon" class="iconfont" :class="`icon-${rightIcon}`" :style="{ color: themeColor }" />
						<span v-if="rightText && !rightIcon" class="tab-nav-bar-right-text" :style="{ color: themeColor }">
							{{ rightText }}
						</span>
					</slot>
				</div>
			</div>
		</div>

		<!-- 占位符（fixed 模式下撑开页面空间） -->
		<div v-if="fixed" class="tab-navbar__placeholder">
			<div class="tab-navbar__placeholder-view" :style="{ height: navbarHeight }" />
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
	dark: { type: Boolean, default: false }, // 是否启用暗黑模式
	title: { type: String, default: '' }, // 导航栏标题文字
	leftText: { type: String, default: '' }, // 左侧按钮文字
	rightText: { type: String, default: '' }, // 右侧按钮文字
	leftIcon: { type: String, default: '' }, // 左侧图标名称
	rightIcon: { type: String, default: '' }, // 右侧图标名称
	fixed: { type: [Boolean, String], default: false }, // 是否固定在页面顶部
	color: { type: String, default: '' }, // 标题文字颜色
	backgroundColor: { type: String, default: '' }, // 导航栏背景颜色
	shadow: { type: [Boolean, String], default: false }, // 是否显示底部阴影
	border: { type: [Boolean, String], default: true }, // 是否显示底部边框
	height: { type: [Number, String], default: 44 }, // 导航栏高度，单位 px
	leftWidth: { type: [Number, String], default: 40 }, // 左侧操作区域宽度，单位 px
	rightWidth: { type: [Number, String], default: 40 }, // 右侧操作区域宽度，单位 px
});

const emit = defineEmits(['clickLeft', 'clickRight', 'clickTitle']);

const getVal = val => (typeof val === 'number' ? val + 'px' : val);

const themeBgColor = computed(() => {
	if (props.dark) return props.backgroundColor || '#333';
	return props.backgroundColor || '#fff';
});

const themeColor = computed(() => {
	if (props.dark) return props.color || '#fff';
	return props.color || '#333';
});

const navbarHeight = computed(() => getVal(props.height));
const leftIconWidth = computed(() => getVal(props.leftWidth));
const rightIconWidth = computed(() => getVal(props.rightWidth));

function onClickLeft() { emit('clickLeft'); }
function onClickRight() { emit('clickRight'); }
function onClickTitle() { emit('clickTitle'); }
</script>

<style lang="scss" scoped>
$nav-height: 44px;

.tab-navbar {
	font-size: 14px;
}

.tab-navbar__content {
	position: relative;
	background-color: transparent;
}

.tab-navbar--fixed {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 998;
}

.tab-navbar--shadow {
	box-shadow: 0 1px 6px #ccc;
}

.tab-navbar--border {
	border-bottom: 1px solid #eee;
}

.tab-navbar__header {
	display: flex;
	flex-direction: row;
	align-items: center;
	height: $nav-height;
	padding: 0 10px;
	font-size: 12px;
}

.tab-navbar__header-btns {
	display: flex;
	flex-wrap: nowrap;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	overflow: hidden;
	cursor: pointer;
}

.tab-navbar__header-btns-left {
	justify-content: flex-start;
}

.tab-navbar__header-btns-right {
	flex-direction: row;
	justify-content: flex-end;
}

.tab-navbar__header-container {
	display: flex;
	flex: 1;
	padding: 0 10px;
	overflow: hidden;
}

.tab-navbar__header-container-inner {
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	overflow: hidden;
}

.tab-nav-bar-text {
	font-size: 14px;
}

.tab-navbar-btn-text {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	line-height: 12px;
}

.tab-nav-bar-right-text {
	font-size: 12px;
}

.tab-navbar__placeholder-view {
	height: $nav-height;
}

.tab-ellipsis-1 {
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
}

.tab-dark {}
</style>
